import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../design-system/tokens/colors';
import { MailIcon } from '@heroicons/react/outline';
import { LoadingOutlined } from '@ant-design/icons';
import type { Creator } from '../../Creators/creatorTypes';

interface CompanionCellProps {
  hasDraft: boolean;
  companionName: string;
  companionId: string;
  companionColor: string;
  creator: Creator;
  onOpenConversation: () => void;
  // AI integration props
  tableGoal?: string;
  projectId?: string;
  tableId?: string;
  columnId?: string;
  onDraftGenerated?: (draft: string) => void;
  isGeneratingDraft?: boolean;
  onGenerateDraft?: () => Promise<void>;
}

const StatusBadge = styled.div<{ $hasDraft: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  user-select: none;
  cursor: pointer;
  transition: all 0.12s ease;
  
  ${props => props.$hasDraft ? `
    background: rgba(99, 91, 255, 0.08);
    color: #635bff;
    
    &:hover {
      background: rgba(99, 91, 255, 0.12);
    }
  ` : `
    background: rgba(0, 0, 0, 0.03);
    color: ${colors.text.secondary};
    
    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  `}
`;

const IconButton = styled.button<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.12s ease;
  flex-shrink: 0;
  margin-left: auto;
  
  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #6b7280;
  }
  
  &:active {
    background: rgba(0, 0, 0, 0.08);
  }
  
  svg {
    width: 13px;
    height: 13px;
  }
`;

const CellContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 0;
`;

export const CompanionCell: React.FC<CompanionCellProps> = ({
  hasDraft,
  companionName,
  companionId,
  companionColor,
  creator,
  onOpenConversation,
  tableGoal,
  projectId,
  tableId,
  columnId,
  onDraftGenerated,
  isGeneratingDraft = false,
  onGenerateDraft
}) => {
  const handleDraftClick = async () => {
    if (hasDraft) {
      // If draft exists, open conversation sidebar
      onOpenConversation();
    } else if (onGenerateDraft && !isGeneratingDraft) {
      // If no draft, generate one
      await onGenerateDraft();
    }
  };

  return (
    <CellContainer>
      {/* Status Badge - Direct draft action */}
      <StatusBadge 
        $hasDraft={hasDraft}
        onClick={handleDraftClick}
        title={
          isGeneratingDraft 
            ? `${companionName} is drafting...` 
            : hasDraft 
              ? `View draft message from ${companionName}` 
              : `Ask ${companionName} to draft message`
        }
        style={{ cursor: isGeneratingDraft ? 'wait' : 'pointer' }}
      >
        {isGeneratingDraft ? (
          <>
            <LoadingOutlined style={{ fontSize: '10px' }} />
            <span>Drafting...</span>
          </>
        ) : (
          <span>{hasDraft ? 'Draft ready' : `Ask ${companionName}`}</span>
        )}
      </StatusBadge>
      
      {/* Email Icon - Opens conversation sidebar */}
      <IconButton 
        $visible={true}
        onClick={(e) => {
          e.stopPropagation();
          onOpenConversation();
        }}
        title="View & edit draft message"
      >
        <MailIcon />
      </IconButton>
    </CellContainer>
  );
};

